import { useCallback, useState } from 'react'

import { AnnotationMetadata } from '@/features/annotation'

import { PendingAnnotationState } from '../types'

interface UseCommentDialogParams {
  documentVersionId: string | undefined
  onSelectionCleared: () => void
  onCommentAdded: (commentId: string, metadata: AnnotationMetadata) => void
}

export const useCommentDialog = ({
  documentVersionId,
  onSelectionCleared,
  onCommentAdded,
}: UseCommentDialogParams) => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [pendingAnnotation, setPendingAnnotation] =
    useState<PendingAnnotationState>({
      metadata: null,
      showBubble: false,
      bubblePosition: null,
    })

  const handleAddComment = useCallback(() => {
    setDialogOpen(true)
  }, [])

  const handleCommentSuccess = useCallback(
    (commentId: string) => {
      if (!pendingAnnotation.metadata) return

      onCommentAdded(commentId, pendingAnnotation.metadata)

      setPendingAnnotation({
        metadata: null,
        showBubble: false,
        bubblePosition: null,
      })
    },
    [pendingAnnotation.metadata, onCommentAdded]
  )

  const handleDialogClose = useCallback(
    (open: boolean) => {
      setDialogOpen(open)
      if (!open) {
        setPendingAnnotation({
          metadata: null,
          showBubble: false,
          bubblePosition: null,
        })
        onSelectionCleared()
      }
    },
    [onSelectionCleared]
  )

  const setPendingBubble = useCallback(
    (metadata: AnnotationMetadata | null, show: boolean) => {
      setPendingAnnotation({
        metadata,
        showBubble: show,
        bubblePosition: null,
      })
    },
    []
  )

  const clearPending = useCallback(() => {
    setPendingAnnotation({
      metadata: null,
      showBubble: false,
      bubblePosition: null,
    })
  }, [])

  return {
    dialogOpen,
    pendingAnnotation,
    setPendingBubble,
    clearPending,
    dialogState: {
      open: dialogOpen,
      documentVersionId,
      annotation: pendingAnnotation.metadata,
      onOpenChange: handleDialogClose,
      onSuccess: handleCommentSuccess,
    },
    pendingBubble: {
      onAddComment: handleAddComment,
    },
  }
}
